import Table from './table.js';
import events from './event.js';
import products from './products.js';
import Storage from './storage.js';
import grade from './resolutionGrade.js';
import imageHelper from './imageHelper.js';
import Logger from './logger.js';
import { textString } from './texts.js';
import TextParser from './textParser.js';
import HelpPopup from './HelpPopup.js';

class Size {
    customizerContentDiv = null;
    table = null;
    wrapperDiv = null;
    resolutionDiv = null;
    helpPopup = null;

    constructor(customizerContentDiv) {
        this.customizerContentDiv = customizerContentDiv;
        if (!customizerContentDiv) {
            Logger.error('Size step failed to load.');
            return;
        }

        this.init();

        this.helpPopup = new HelpPopup({
            parentElement: this.customizerContentDiv,
            textKey: 'size_low_resolution_grade_helptext',
            defaultText: _('Selected size has a low resolution grade.'),
            horizontalAlign: 'center',
            placement: 'under',
            offsetY: -35,
            offsetX: 0,
        });


    }

    async init() {
        await this.createWrapper();
        await this.createTable();
        this.addListeners();
    }

    async createWrapper() {
        if (this.wrapperDiv) {
            // If already initialized, just show the wrapper
            this.wrapperDiv.style.display = 'flex';
            return;
        }
        this.wrapperDiv = document.createElement('div');
        this.wrapperDiv.classList.add('mscp-size-wrapper');
        this.customizerContentDiv.appendChild(this.wrapperDiv);

        // create resolution div
        this.resolutionDiv = document.createElement('div');
        this.resolutionDiv.classList.add('mscp-resolution-wrapper', 'mscp-table');
        this.wrapperDiv.appendChild(this.resolutionDiv);
    }

    async createTable() {
        // create size list
        const sizes = await products.getSizes();
        this.table = new Table({
            'wrapperDiv': this.resolutionDiv,
            'terms': sizes,
            'type': 'size',
            'selectCallback': async (el, value, oldValue) => {
                const noSizeSelectedText = document.getElementById('mscp-text-adjust_mode_no_size_selected');
                if (noSizeSelectedText) {
                    noSizeSelectedText.style.display = 'none'; // Remove "no size selected" text if it exists
                }
                // set current resolution grade
                const gradeValue = el.querySelector('.mscp-resolution-stars')?.dataset.grade;
                if (gradeValue !== undefined) {
                    Storage.save('currentResolutionGrade', gradeValue);
                    events.trigger('currentResolutionGradeUpdated', gradeValue);
                    // Logger.clear();
                    if (gradeValue <= 1) {
                        // Logger.error('Selected size has a low resolution grade.');
                        this.helpPopup.show(el);
                        console.error(`Selected size has a low resolution grade of ${gradeValue}.`);
                    }
                    else {
                        this.helpPopup.hide();
                    }
                    // else if (gradeValue <= 2) {
                    //     Logger.warn('Selected size has a moderate resolution grade.');
                    // }
                }

                await events.trigger('updateCropSize', false); // Ensure cropper is updated after size list changes
                events.trigger('updateNavigation'); // Trigger navigation update
                Storage.save('thumbnailUrlDirty', true); // Mark thumbnail as dirty to trigger re-upload
            },
            'preCreateTitleCallback': (term) => {
            },
            'postCreateTitleCallback': async (term) => {
                const array = [];

                // const resolution = document.createElement('span');
                // resolution.classList.add('mscp-resolution-stars', 'mscp-column', 'mscp-post-create');
                // resolution.dataset.grade = '0';
                // resolution.innerHTML = "☆☆☆☆☆"; // Default to empty stars
                // array.push(resolution);

                const price = document.createElement('span');
                price.classList.add('mscp-term-price', 'mscp-column', 'mscp-post-create');
                const fromPrice = await products.getFromPrice(Storage.get('paper'), Storage.get('presentation'), term.value);
                price.textContent = fromPrice ? fromPrice : '';
                array.push(price);

                return array;
            },
            'overrideDescription': (term) => {
                const resolutionWrapper = document.createElement('span');
                resolutionWrapper.classList.add('mscp-item-description', 'mscp-row');
                const resolutionStars = document.createElement('span');
                resolutionStars.classList.add('mscp-resolution-stars');
                resolutionStars.dataset.grade = '0';
                resolutionStars.innerHTML = "☆☆☆☆☆"; // Default to empty stars
                resolutionWrapper.appendChild(resolutionStars);
                const resolutionText = document.createElement('span');
                resolutionText.classList.add('mscp-resolution-text');
                resolutionText.innerHTML = '';
                resolutionWrapper.appendChild(resolutionText);
                return resolutionWrapper;
            }

            // 'selectRule': (el) => {
            //     const stars = el.querySelector('.mscp-resolution-stars')?.dataset.grade;
            //     if (stars !== undefined) {
            //         Logger.warn('No resolution grade found for size selection');
            //         return false; // Prevent selection if no grade is found
            //     }
            //     return true; // Allow selection if grade is valid
            // }
        });
    }

    addListeners() {
        // Add event listener for step changes
        events.add('stepChanged', (step) => {
            if (step === 'size') {
                this.update();
                this.updateResolutionGrades();
            }
        });

        // Add event listener for crop box updates
        events.add('cropBoxUpdated', () => {
            this.updateResolutionGrades();
        }, { throttle: 300 });

        events.add('reInitUpload', () => {
            this.table.deselectAll();
        });

        events.add('setSize', (size) => {
            this.table.selectItemByValue(size);
        });

    }

    async updateResolutionGrades() {
        // const size = await Storage.get('size');
        const presentation = await Storage.get('presentation');
        const orientation = await Storage.get('orientation');
        const cropBox = await Storage.get('cropBox');
        const size = await Storage.get('size');
        const imageResolution = await imageHelper.getActiveImageResolution();
        if (!imageResolution) {
            Logger.debug('No image resolution found for resolution grading');
            return;
        }
        this.table.items?.forEach(async (item) => {
            const itemSize = item.rowDiv.getAttribute('data-value');
            const paper = await Storage.get('paper');
            grade.get(
                imageResolution,
                itemSize,
                presentation,
                orientation,
                cropBox,
                paper
            ).then((newGrade) => {
                // Assuming newGrade is a number between 0 and 5
                if (newGrade === null || newGrade < 0 || newGrade > 5) {
                    Logger.error('Invalid resolution grade:', newGrade);
                    return;
                }
                // render resolution grade ☆☆☆☆☆ or ★★★★★ based on newGrade
                const resolutionDiv = item.rowDiv.querySelector('.mscp-resolution-stars');
                if (resolutionDiv) {
                    resolutionDiv.dataset.grade = newGrade;
                    resolutionDiv.innerHTML = '★'.repeat(newGrade) + '☆'.repeat(5 - newGrade);
                }
                const resolutionText = textString(`resolution_label_${newGrade}`);
                const resolutionTextDiv = item.rowDiv.querySelector('.mscp-resolution-text');

                if (resolutionTextDiv) {
                    resolutionTextDiv.innerHTML = resolutionText;
                    TextParser.link(resolutionTextDiv, (attrs) => {
                        events.trigger('openAdditionalInfoPopup', {
                            text_prop_name: `resolution_info_${newGrade}`
                        });
                        return false; // Prevent default link behavior
                    });
                }
                if (itemSize == size) {
                    Storage.save('currentResolutionGrade', newGrade);
                    events.trigger('currentResolutionGradeUpdated', newGrade);
                }
                // Logger.debug('>>> new grade:', newGrade, 'for product size:', productSize, 'and image resolution:', imageResolution);
            }).catch((error) => {
                Logger.error('Error calculating resolution grade:', error);
            });
        });
    }

    async update() {
        const currentProductIds = await products.getAttributeHierarchyProductIDs();

        // Get all products and filter by current product IDs and their sizes
        const allProducts = await products.getProducts();
        const matchingEntries = allProducts.filter((product) => currentProductIds.includes(Number(product.value)));
        const allSizes = matchingEntries.flatMap((product) => product.variations.map(v => v.attributes.attribute_pa_size));
        const currentSizes = [...new Set(allSizes)]; // Get unique sizes from all matching products

        this.table.updateVisibleItems(currentSizes);

        // Nytt steg 1: the order of the sizes is the order of the pa_size terms (data), and the preselected size is
        // the "Default size" setting (window.mscp.defaults.size) when the product has it, else its first size.
        if (document.querySelector('.mscp-popup-content.mscp-up2-on') && !Storage.get('size') && currentSizes.length) {
            const wanted = window.mscp?.defaults?.size;
            this.table.clickItemByValue(currentSizes.includes(wanted) ? wanted : currentSizes[0]);
        }
    }
}

export default Size;